import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@chakra-ui/react';
import {
  FaRegClock,
  FaSearch,
} from "react-icons/fa";
import { useColorModeValue } from '../../components/ui/color-mode';
import FitTabs from '../../components/shared/tabs/Tabs';
import { Header } from '../../components/shared/IndexHeader';
import { Overview } from '../../components/shared/OverviewTab';
import { ListComponent } from '../../components/shared/ListTab';
import styles from './complaint.module.css';
import { darkMode, lightMode } from '../../theme/config';
import { insightsPath, listPath } from '../../utils/constants/insightsPath';
import { useDispatch, useSelector } from 'react-redux';
import { resetFilterData } from '../../store/filterSlice';
import { setShowModelsState } from '../../store/modelsSlice';
import { setIsOnComplaintRoute } from '../../store/complaintSlice';
import { formatComplaintsData } from '../../utils/dataFormatters';
import { dateRangeOptions } from '../home/dashboardConstants';
import SelectBox from '../../components/shared/fields/SelectBox';
import { useForm } from 'react-hook-form';
import ViewData from '../../components/shared/dialogs/ViewDataDialog';
import { useCustomerActions } from '../customers/useCustomerActions';
import { getComplaintActions } from '../customers/customerConstants';
import UpdateComplaintDialog from '../customers/UpdateComplaintDialog';

const columns = [
  { key: "complaintId", label: "Complaint ID", width: "7%", textAlign: "center" },
  { key: "complaintReferenceNumber", label: "Ref No.", width: "6%", textAlign: "center" },
  { key: "customerEmail", label: "Email ID", width: "8%", textAlign: "center" },
  { key: "customerContact", label: "Contact No.", width: "8%", textAlign: "center" },
  { key: "serialNo", label: "Serial No.", width: "6%", textAlign: "center" },
  { key: "title", label: "Title", width: "9%", textAlign: "center" },
  { key: "complaintCategory", label: "Complaint Category", width: "12%", textAlign: "center" },
  { key: "complaintSubCategory", label: "Complaint Sub Category", width: "12%", textAlign: "center" },
  { key: "priority", label: "Priority", width: "6%", textAlign: "center" },
  { key: "status", label: "Status", width: "5%", textAlign: "center" },
  { key: "assignedTo", label: "Assigned To", width: "6%", textAlign: "center" },
  { key: "createdDate", label: "Created Date", width: "9%", textAlign: "center" },
  { key: "lastUpdatedDate", label: "Last Updated", width: "9%", textAlign: "center" },
  { key: "action", label: "", width: "0%", textAlign: "center" },
];

const ComplaintIndex = () => {

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(resetFilterData({ key: listPath.COMPLAINTS }));
    dispatch(setIsOnComplaintRoute(true));
    return () => {
      dispatch(setIsOnComplaintRoute(false));
    };
  }, [])

  useEffect(() => {
    dispatch(setShowModelsState(false));
  }, [dispatch]);

  const [activeTab, setActiveTab] = useState("tab-1");
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const borderColor = useColorModeValue(lightMode.containerBorderColor, darkMode.containerBorderColor);
  const textColor = useColorModeValue(lightMode.textColor, darkMode.textColor);
  const { control } = useForm();
  const { serviceRequestCategories, serviceRequestSubcategories } = useSelector((state) => state.categories || {
    serviceRequestCategories: [],
    serviceRequestSubcategories: {}
  });

  const complaintsList = useSelector((state) => state.list[listPath.COMPLAINTS] || {
    rows: [],
  });

  const {
    handleEditComplaint,
    updateComplaintDialog,
    closeUpdateComplaintDialog
  } = useCustomerActions();

  const formattedComplaints = formatComplaintsData(
    complaintsList.rows,
    serviceRequestCategories,
    serviceRequestSubcategories
  );

  const statusOptions = [
    { value: 'open', label: 'Open' },
    { value: 'inprogress', label: 'In Progress' },
    { value: 'resolved', label: 'Resolved' }
  ];

  const categoryData = useSelector((state) => state.categories || {
    serviceRequestCategories: [],
    serviceRequestSubcategories: {}
  });

  // Handle view action
  const handleViewComplaint = (row) => {
    setSelectedComplaint(row);
    setIsViewDialogOpen(true);
  };

  // Handler function to close ViewData dialog
  const handleCloseViewDialog = () => {
    setIsViewDialogOpen(false);
    setSelectedComplaint(null);
  };

  // Handler function to update complaint data from ViewData dialog
  const handleUpdateComplaint = (updatedData) => {
    handleCloseViewDialog();
  };

  const complaintActions = getComplaintActions(handleEditComplaint);

  const tabsData = useMemo(() => [
    {
      value: "tab-1",
      label: <><FaRegClock />Overview</>,
      panel: (
        <Overview
          path={insightsPath.COMPLAINTS}
          source="COMPLAINT"
        />
      )
    },
    {
      value: "tab-2",
      label: <><FaSearch />Complaints List</>,
      panel: (
        <ListComponent
          borderColor={borderColor}
          path={listPath.COMPLAINTS}
          searchPlaceholder="Search Complaints..."
          columns={columns}
          actions={complaintActions}
          formatData={(rows) => formatComplaintsData(
            rows,
            serviceRequestCategories,
            serviceRequestSubcategories,
          )}
          statusOptions={statusOptions}
          categoryOptions={serviceRequestCategories}
          subCategoryOptions={serviceRequestSubcategories}
          showSearchBar={true}
          source="COMPLAINTS"
        />
      )
    }
  ], [
    formattedComplaints,
    borderColor,
    categoryData.serviceRequestCategories,
    categoryData.serviceRequestSubcategories,
    handleViewComplaint
  ]);

  const handleTabChange = (newTabValue) => {
    setActiveTab(newTabValue.value);
  };

  return (
    <Box className={styles.container}>
      <Box display={"flex"} justifyContent={"space-between"}>
        <Box width={"70%"}>
          <Header
            title={activeTab === "tab-1" ? "Complaints Management" : "Complaints List"}
            subtitle={activeTab === "tab-1"
              ? "View and manage customer complaints"
              : "All customer complaints submitted through the mobile app"}
            textColor={textColor}
          />
        </Box>
        <Box width={"20%"}>
          <SelectBox
            label="Date Range"
            name="dateRange"
            control={control}
            items={dateRangeOptions}
            placeholder="Last 30 Days"
            disabled={true}
          />
        </Box>
      </Box>
      <FitTabs
        tabs={tabsData}
        value={activeTab}
        onChange={handleTabChange}
        variant="enclosed"
        width="100%"
        fitted
      />

      <ViewData
        isOpen={isViewDialogOpen}
        onClose={handleCloseViewDialog}
        rowData={selectedComplaint}
        onUpdate={handleUpdateComplaint}
        serviceRequestCategories={serviceRequestCategories}
        serviceRequestSubcategories={serviceRequestSubcategories}
      />
      <UpdateComplaintDialog isOpen={updateComplaintDialog} onClose={closeUpdateComplaintDialog} />
    </Box>
  );
};

export default ComplaintIndex;